import { forwardRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  Flex,
  Portal,
  AlertProps,
} from '@frontend/legacy-components';

import { logoutAll } from 'modules/settings/userProfile/loginActivity/data/actions';

import { useInterval } from 'utils/hooks';
import { getTokenExpiry } from 'utils/token';

import {
  USER_TIMEOUT_INITIAL_REFRESH_INTERVAL,
  USER_TIMEOUT_INTERVAL_PADDING,
  USER_TIMEOUT_REFRESH_INTERVAL,
  USER_TIMEOUT_WARN_INTERVAL,
} from './constants';

export const UserTimeoutWarning = forwardRef<HTMLDivElement, AlertProps>(
  function UserTimeoutWarning(props, ref) {
    const dispatch = useDispatch();

    const [checkTimeoutInterval, setCheckTimeoutInterval] = useState<
      number | null
    >(USER_TIMEOUT_INITIAL_REFRESH_INTERVAL);
    const [warnUserTimeout, setWarnUserTimeout] = useState(false);
    const [timeUntilTimeout, setTimeUntilTimeout] = useState<number | null>(
      null,
    );

    const shouldWarnUserAboutTimeout = () => {
      const tokenExpiry = getTokenExpiry();
      if (tokenExpiry) {
        const now = Date.now();
        const tokenExpiryInt = Math.max(
          parseInt(tokenExpiry) - now - USER_TIMEOUT_INTERVAL_PADDING,
          0,
        );
        setTimeUntilTimeout(tokenExpiryInt);

        if (tokenExpiryInt < USER_TIMEOUT_REFRESH_INTERVAL) {
          setCheckTimeoutInterval(null);
          dispatch(logoutAll.request(true));
          return;
        }

        const timeoutWithinWarnInterval =
          tokenExpiryInt <= USER_TIMEOUT_WARN_INTERVAL;
        if (timeoutWithinWarnInterval && !warnUserTimeout) {
          setWarnUserTimeout(true);
          setCheckTimeoutInterval(USER_TIMEOUT_REFRESH_INTERVAL);
        } else if (!timeoutWithinWarnInterval) {
          setCheckTimeoutInterval(tokenExpiryInt - USER_TIMEOUT_WARN_INTERVAL);
        }
      }
    };

    useInterval(shouldWarnUserAboutTimeout, checkTimeoutInterval);

    if (!warnUserTimeout || timeUntilTimeout === null) {
      return null;
    }

    return (
      <Portal>
        <Alert
          ref={ref}
          alignItems="center"
          border="none"
          status="warning"
          zIndex="toast"
          {...props}
        >
          <AlertIcon />
          <Flex w="100%" direction="row" alignItems="center">
            <AlertTitle>
              Your session will expire in{' '}
              {new Date(timeUntilTimeout).toISOString().slice(15, -5)}
            </AlertTitle>
            <AlertDescription>
              Please save any in-progress changes and reauthenticate.
            </AlertDescription>
            <Flex flex={1} justifyContent="end">
              <Button
                onClick={() => dispatch(logoutAll.request())}
                size="sm"
                variant="link"
              >
                Log out to reauthenticate now
              </Button>
            </Flex>
          </Flex>
        </Alert>
      </Portal>
    );
  },
);
